/**
 * AI 문서 아카이브 — 공통 촬영/업로드 + 인식결과 확인 + 목록 패널
 * OCR/계약정보/인사정보 3개 카드가 이 컴포넌트를 category만 바꿔 재사용한다
 * (docTypesByCategory 기준 — 새 docType이 늘어도 이 컴포넌트는 그대로다, §3 매핑 어댑터 원칙과 동일선상).
 *
 * sensitive=true(인사정보)면 목록에서 주민등록번호류(field: 'rrn')를 마스킹해서 보여준다.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import useTenantCode from 'hooks/useTenantCode';
import { ActionButton } from 'styles/common.styles';
import { showAlert } from 'utils/toastBridge';
import {
  docTypesByCategory,
  docTypeOf,
  createDocUid,
  resizeImageFile,
  estimateImageTokens,
  simulateExtraction,
  loadDocInstancesByCategory,
  saveDocInstance,
  removeDocInstance,
  formatAmt,
  maskRrn,
} from 'archive/archiveDocStore';

const Wrap = styled.div`
  display: grid;
  grid-template-columns: minmax(280px, 380px) 1fr;
  gap: 12px;
  align-items: start;
  font-size: 12px;
  @media (max-width: 900px) { grid-template-columns: 1fr; }
  .box { border: 1px solid #d9dde3; border-radius: 4px; background: #fff; padding: 8px 10px; display: flex; flex-direction: column; gap: 8px; }
  .title { font-weight: 700; font-size: 13px; }
  .row { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; }
  .hint { color: #5b6472; }
  .notice { color: #b45309; background: #fffbeb; border: 1px solid #fde68a; border-radius: 4px; padding: 4px 6px; }
  img.preview { max-width: 100%; border: 1px solid #e5e8ee; }
  input[type='text'], input[type='date'], select {
    height: 24px; font-size: 12px; border: 1px solid #c5cad3; border-radius: 3px; padding: 0 4px;
  }
  table { width: 100%; border-collapse: collapse; }
  th, td { border: 1px solid #d9dde3; padding: 2px 4px; }
  th { background: #f3f5f8; }
`;

const AMOUNT_RE = /amount|금액|가액|세액/;

const DocCaptureArchivePanel = ({
  category,
  panelTitle,
  uploadHint,
  sensitive = false,
  sensitiveNotice,
  afterSaveHint,
}) => {
  const tenantCode = useTenantCode();
  const fileRef = useRef(null);
  const docTypeOptions = useMemo(() => docTypesByCategory(category), [category]);
  const [docType, setDocType] = useState(docTypeOptions[0]?.value || '');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [fields, setFields] = useState({});
  const [docDate, setDocDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [docs, setDocs] = useState(() => loadDocInstancesByCategory(tenantCode, category));

  useEffect(() => {
    setDocs(loadDocInstancesByCategory(tenantCode, category));
  }, [tenantCode, category]);

  const currentType = docTypeOf(docType);
  const tokenEstimate = preview ? estimateImageTokens(preview.width, preview.height) : 0;

  const pickFile = async (f) => {
    if (!f) return;
    try {
      const resized = await resizeImageFile(f, 1280);
      setFile(f);
      setPreview(resized);
      setFields({});
    } catch (e) {
      showAlert(e?.message || '이미지를 처리하지 못했습니다.', 'error');
    }
  };

  const runExtraction = async () => {
    if (!preview) return;
    setBusy(true);
    try {
      const result = await simulateExtraction(docType, {
        width: preview.width,
        height: preview.height,
        fileName: file?.name,
      });
      setFields(result.fields);
      showAlert(result.note, 'warning');
    } finally {
      setBusy(false);
    }
  };

  const setField = (field, value) => setFields((prev) => ({ ...prev, [field]: value }));

  const save = () => {
    if (!currentType) return;
    const hasAny = currentType.fields.some((f) => String(fields[f.field] || '').trim());
    if (!hasAny) {
      showAlert('최소 한 개 이상의 필드를 입력하세요.', 'warning');
      return;
    }
    const doc = {
      docUid: createDocUid(),
      tenantCode,
      docType,
      inputMethod: 'CAPTURE_OR_UPLOAD',
      fileName: file?.name || '',
      previewDataUrl: preview?.dataUrl || '',
      tokenEstimate,
      docDate,
      fields,
      status: 'CONFIRMED',
      createdAt: new Date().toISOString(),
    };
    saveDocInstance(tenantCode, doc);
    setDocs(loadDocInstancesByCategory(tenantCode, category));
    setFile(null);
    setPreview(null);
    setFields({});
    if (fileRef.current) fileRef.current.value = '';
    showAlert(afterSaveHint || '아카이브에 저장했습니다.', 'success');
  };

  const del = (docUid) => {
    removeDocInstance(tenantCode, docUid);
    setDocs(loadDocInstancesByCategory(tenantCode, category));
  };

  const displayFieldValue = (field, value) => {
    if (sensitive && field === 'rrn') return maskRrn(value);
    return value || '-';
  };

  return (
    <Wrap>
      <div className="box">
        <div className="title">1. 문서 이미지</div>
        {sensitive && sensitiveNotice && <div className="notice">⚠ {sensitiveNotice}</div>}
        <div className="row">
          <label>
            문서종류{' '}
            <select value={docType} onChange={(e) => { setDocType(e.target.value); setFields({}); }}>
              {docTypeOptions.map((d) => (
                <option key={d.value} value={d.value}>{d.label}</option>
              ))}
            </select>
          </label>
        </div>
        <div className="row">
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={(e) => pickFile(e.target.files?.[0])}
          />
        </div>
        <span className="hint">{uploadHint || '모바일에서는 카메라 촬영, PC에서는 파일 선택이 열립니다. 선택 즉시 브라우저에서 리사이즈합니다.'}</span>
        {preview && (
          <>
            <img className="preview" src={preview.dataUrl} alt="문서 미리보기" />
            <span className="hint">
              리사이즈 {preview.width}×{preview.height} · 예상 입력 토큰 약 {tokenEstimate.toLocaleString('ko-KR')}
            </span>
          </>
        )}
        <div className="row">
          <ActionButton type="button" disabled={!preview || busy} onClick={runExtraction}>
            {busy ? '인식 중…' : '🔍 자동 인식'}
          </ActionButton>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div className="box">
          <div className="title">2. {panelTitle || '인식 결과 확인·입력'} — {currentType?.label}</div>
          <div className="row">
            <label>문서일자 <input type="date" value={docDate} onChange={(e) => setDocDate(e.target.value)} /></label>
          </div>
          <table>
            <tbody>
              {(currentType?.fields || []).map((f) => (
                <tr key={f.field}>
                  <th style={{ width: 120 }}>{f.label}{sensitive && f.field === 'rrn' ? ' 🔒' : ''}</th>
                  <td>
                    <input
                      type="text"
                      style={{ width: '100%' }}
                      value={fields[f.field] || ''}
                      onChange={(e) => setField(f.field, e.target.value)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="row">
            <ActionButton type="button" onClick={save}>💾 아카이브 저장</ActionButton>
          </div>
        </div>

        <div className="box">
          <div className="title">3. 저장된 문서 ({docs.length})</div>
          <table>
            <thead>
              <tr><th>일자</th><th>종류</th><th>요약</th><th>{sensitive ? '식별정보' : '금액'}</th><th /></tr>
            </thead>
            <tbody>
              {docs.length === 0 && <tr><td colSpan={5} className="hint">저장된 문서가 없습니다.</td></tr>}
              {docs.map((d) => {
                const type = docTypeOf(d.docType);
                const primaryField = type?.fields?.[0];
                const secondaryField = sensitive
                  ? type?.fields?.find((f) => f.field === 'rrn')
                  : type?.fields?.find((f) => AMOUNT_RE.test(`${f.field}${f.label}`));
                return (
                  <tr key={d.docUid}>
                    <td>{d.docDate}</td>
                    <td>{type?.label}</td>
                    <td>{primaryField?.label}: {displayFieldValue(primaryField?.field, d.fields?.[primaryField?.field])}</td>
                    <td style={{ textAlign: sensitive ? 'left' : 'right' }}>
                      {secondaryField
                        ? sensitive
                          ? displayFieldValue('rrn', d.fields?.[secondaryField.field])
                          : formatAmt(d.fields?.[secondaryField.field])
                        : '-'}
                    </td>
                    <td><ActionButton type="button" onClick={() => del(d.docUid)}>삭제</ActionButton></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </Wrap>
  );
};

export default DocCaptureArchivePanel;
